/**
 * Pure helpers + shared style tokens for the CSR "New booking" modal
 * (BON-142): the picked-workshop shape, payment-mode type, validation, and
 * the money summary. Split out of the form/fields/shell files so none of them
 * trips the repo's 300-line cap.
 */

/** The subset of a `Workshop` (see `../../../api/workshops`) the booking form
 *  needs, mapped by the picker so this modal doesn't couple to the full list
 *  shape. */
export interface PickedWorkshop {
  id: string;
  /** Workshop type display name, e.g. "Dwarf Jade". */
  typeName: string;
  treeSpeciesName: string | null;
  venueName: string;
  venueCity: string | null;
  venueState: string | null;
  /** The venue-local wall clock (naive `localStartDatetime`, no `Z`) — this
   *  field is DISPLAY-ONLY here, so it carries the wall clock rather than the
   *  absolute instant (BON-386). Format via `WorkshopPickerHelpers`
   *  (`formatPickerDate` / `formatPickerTime`), never as a UTC instant. */
  startsAt: string;
  priceCents: number;
  /** Live seats remaining (`Workshop.available`) — bounds the ticket stepper. */
  seatsLeft: number;
}

export type PaymentMode = 'reservation' | 'comp';

/**
 * Can a CSR reserve seats for a customer to pay for themselves? **Yes, as of
 * BON-382.**
 *
 * This replaced the hosted Stripe "payment link" that BON-379 deleted along with
 * the whole Checkout Sessions path. A RESERVATION (design decision D5) holds the
 * seats on the checkout record and emails the customer a link to the ordinary
 * checkout — one expiry instead of two clocks that can disagree, and no unpaid
 * rows polluting the bookings table.
 */
export const RESERVATIONS_ENABLED = true;

/** The mode the form opens on. Reserving is the common case; comping is the
 *  privileged exception. */
export const DEFAULT_PAYMENT_MODE: PaymentMode = RESERVATIONS_ENABLED ? 'reservation' : 'comp';

export type CreateBookingSuccess =
  | { kind: 'comp'; bookingId: string; confirmationCode: string | null }
  | {
      kind: 'reservation';
      /**
       * NO confirmation code and NO link, both deliberately.
       *
       * A reservation is not a booking, so it has no confirmation code until the
       * customer pays. And the link is never shown to staff (ADR-0023): a
       * copyable one ends up pasted into Slack, and a CSR opening it to check it
       * works would burn the customer's ten-minute window, since nothing
       * distinguishes their browser from the customer's.
       */
      expiresAt: string;
      /** Where the link went, so the toast/dialog can say so. */
      email: string;
      /** True when a double-submit was collapsed into the reservation the first
       *  click already made — worth saying, so nobody creates a second one. */
      duplicate: boolean;
      /** False when the email did NOT go out. The seats are held either way, but
       *  the link is gone for good, so the caller must say so plainly. */
      emailed: boolean;
    };

/** "Thursday at 3:14 PM" — enough for a CSR to say it out loud on the phone. */
export function formatHoldUntil(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'the time shown on the reservation';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit',
  }).format(when);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email.trim());
}

/** Validate the create-booking form; returns an error string or null. Mirrors
 *  the backend's own validation (a workshop + valid email are always
 *  required; a comp additionally requires a reason) so the CTA disables
 *  before a round-trip, but the server remains the source of truth. */
export function validateCreateBookingForm(args: {
  workshop: PickedWorkshop | null;
  email: string;
  tickets: number;
  mode: PaymentMode;
  reason: string;
}): string | null {
  const { workshop, email, tickets, mode, reason } = args;
  if (!workshop) return 'Choose a workshop to continue.';
  if (!isValidEmail(email)) return 'A valid email is required.';
  if (!Number.isInteger(tickets) || tickets < 1) return 'At least 1 ticket is required.';
  if (tickets > workshop.seatsLeft)
    return `Only ${workshop.seatsLeft} seats left on this workshop.`;
  if (mode === 'comp' && !reason.trim()) return 'A reason is required for a comp booking.';
  return null;
}

/** Integer cents → localized currency string, e.g. `$145.80`. */
export function formatMoney(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

/**
 * Money summary shown above the CTA. `kind`:
 *   - `'none'` — no workshop picked yet; nothing to show.
 *   - `'free'` — comp mode; always $0 regardless of tickets/price.
 *   - `'estimate'` — payment-link mode. Only the ticket subtotal is stated here.
 *     The service fee and sales tax are priced by the server when the customer
 *     opens checkout, so they are never estimated in the browser.
 *     `serviceFeeEnabled` decides whether the fee is mentioned at all; it is
 *     null until the booking config loads (or if it fails), and the summary
 *     then mentions it in case it applies.
 */
export type MoneySummary =
  | { kind: 'none' }
  | { kind: 'free' }
  | { kind: 'estimate'; subtotalCents: number; serviceFeeEnabled: boolean | null };

export function computeMoneySummary(
  workshop: PickedWorkshop | null,
  tickets: number,
  mode: PaymentMode,
  serviceFeeEnabled: boolean | null = null,
): MoneySummary {
  if (mode === 'comp') return { kind: 'free' };
  if (!workshop) return { kind: 'none' };
  return {
    kind: 'estimate',
    subtotalCents: workshop.priceCents * Math.max(tickets, 0),
    serviceFeeEnabled,
  };
}

export const inputClass =
  'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9] border-stone-300 dark:border-stone-600';
export const labelClass = 'block text-sm font-medium text-stone-700 dark:text-stone-400 mb-1';
